import { useState, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Autocomplete,
} from '@mui/material';
import { correspondenceTypeSchema } from '../schemas/correspondenceTypeSchema';
import { extractErrorMessage } from '../../../utils/errorUtils';
import areaService from '../../areas/services/areaService';
import { userService } from '../../users';

const CorrespondenceTypeModalForm = ({ open, onClose, onSave, correspondenceType, companies, preselectedCompanyId }) => {
  const [error, setError] = useState('');
  const [areas, setAreas] = useState([]);
  const [users, setUsers] = useState([]);

  const resolvedCompanyId = correspondenceType?.companyId || preselectedCompanyId || '';

  useEffect(() => {
    if (!open) return;
    if (!resolvedCompanyId) return;
    areaService.getAll({ companyId: resolvedCompanyId, limit: 500 }).then((res) => {
      setAreas(res.data || []);
    }).catch(() => setAreas([]));
    userService.getAll({ companyId: resolvedCompanyId, limit: 200 }).then((res) => {
      setUsers(res.data || []);
    }).catch(() => setUsers([]));
  }, [open, resolvedCompanyId]);

  const initialValues = {
    name: correspondenceType?.name || '',
    description: correspondenceType?.description || '',
    companyId: resolvedCompanyId,
    expiration: correspondenceType?.expiration || '',
    public: correspondenceType?.public ?? true,
    areaId: correspondenceType?.areaId || '',
    assignedUserId: correspondenceType?.assignedUserId || '',
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setError('');
      await onSave({
        ...values,
        expiration: values.expiration ? parseInt(values.expiration) : null,
        areaId: values.areaId || null,
        assignedUserId: values.assignedUserId || null,
      });
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {correspondenceType ? 'Editar Tipo de Correspondencia' : 'Nuevo Tipo de Correspondencia'}
      </DialogTitle>

      <Formik
        initialValues={initialValues}
        validationSchema={correspondenceTypeSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ errors, touched, isSubmitting, values, setFieldValue }) => (
          <Form>
            <DialogContent>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Grid container spacing={2}>
                {/* Empresa */}
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    select
                    name="companyId"
                    label="Empresa *"
                    fullWidth
                    value={values.companyId}
                    onChange={(e) => setFieldValue('companyId', e.target.value)}
                    error={touched.companyId && Boolean(errors.companyId)}
                    helperText={touched.companyId && errors.companyId}
                    disabled={!!correspondenceType || !!preselectedCompanyId}
                  >
                    <MenuItem value="">Seleccione una empresa</MenuItem>
                    {companies.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((company) => (
                      <MenuItem key={company.id} value={company.id}>
                        {company.name} ({company.short})
                      </MenuItem>
                    ))}
                  </Field>
                </Grid>

                {/* Nombre */}
                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    name="name"
                    label="Nombre del Tipo *"
                    fullWidth
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                  />
                </Grid>

                {/* Expiración */}
                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    name="expiration"
                    label="Expiración (días)"
                    type="number"
                    fullWidth
                    inputProps={{ min: 1 }}
                    error={touched.expiration && Boolean(errors.expiration)}
                    helperText={(touched.expiration && errors.expiration) || 'Días antes de que la correspondencia de este tipo expire'}
                  />
                </Grid>

                {/* Descripción */}
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    name="description"
                    label="Descripción"
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={10}
                    error={touched.description && Boolean(errors.description)}
                    helperText={touched.description && errors.description}
                  />
                </Grid>

                {/* Público */}
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.public}
                        onChange={(e) => setFieldValue('public', e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">Público</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Visible en el portal de correspondencia externo
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>

                {/* Área */}
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={areas.slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''))}
                    getOptionLabel={(o) => o.name || ''}
                    value={areas.find(a => String(a.id) === String(values.areaId)) || null}
                    onChange={(_, val) => setFieldValue('areaId', val ? val.id : '')}
                    isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                    noOptionsText="Sin áreas disponibles"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Área"
                        error={touched.areaId && Boolean(errors.areaId)}
                        helperText={(touched.areaId && errors.areaId) || 'Área a la que se entregará la correspondencia'}
                      />
                    )}
                  />
                </Grid>

                {/* Persona encargada */}
                <Grid item xs={12}>
                  <TextField
                    select
                    name="assignedUserId"
                    label="Persona encargada"
                    fullWidth
                    value={values.assignedUserId}
                    onChange={(e) => setFieldValue('assignedUserId', e.target.value)}
                    helperText="Persona que recibirá las correspondencias creadas desde el portal externo con este tipo"
                  >
                    <MenuItem value="">— Sin asignar —</MenuItem>
                    {users.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.name} ({user.email})
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button onClick={onClose} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button type="submit" variant="contained" disabled={isSubmitting}>
                {isSubmitting ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default CorrespondenceTypeModalForm;
